
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Role, PermissionLevel } from "./types";

export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles")
        .select("*")
        .order("name");

      if (error) throw error;
      return data as Role[];
    },
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      name, 
      description, 
      permissions 
    }: { 
      name: string, 
      description?: string,
      permissions?: { resource_id: string, permission: PermissionLevel }[]
    }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: { name, description, permissions, action: "create_role" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Role created successfully");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role_permissions"] });
    },
    onError: (error) => {
      toast.error(`Failed to create role: ${error.message}`);
    }
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, name, description }: { id: string, name: string, description?: string }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: { id, name, description, action: "update_role" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Role updated successfully");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      toast.error(`Failed to update role: ${error.message}`);
    }
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: { id, action: "delete_role" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Role deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      toast.error(`Failed to delete role: ${error.message}`);
    }
  });
};
